import React, { FC, useState } from 'react';
import { Button, Popover } from 'antd';
import { WarningCard } from 'common/components/WarningCard';
import { Divider } from 'common/components/Divider';
import { DEFAULT_VALUE_1 } from 'common/config';
import { ISupplyShort } from 'common/models';
import { UserHooks } from 'common/hooks';
import { FloatingActionBar } from 'common/components';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ReactComponent as CompletedIcon } from 'app/assets/images/redesign/completed.svg';
import { ReactComponent as CancelledIcon } from 'app/assets/images/redesign/cancelled.svg';
import { ReactComponent as CalendarIcon } from 'app/assets/images/redesign/calendar.svg';
import { ReactComponent as EditIcon } from 'app/assets/images/redesign/edit.svg';
import { ReactComponent as DeliveryIcon } from 'app/assets/images/redesign/delivery.svg';
import { ReactComponent as ArrowUpShortIcon } from 'app/assets/images/redesign/arrow-up-short.svg';
import { ReactComponent as ArrowDownShortIcon } from 'app/assets/images/redesign/arrow-down-short.svg';
import { RequestSupplyChangeDeliveryDateModal } from 'entities/Request/components/RequestSupplyChangeDeliveryDateModal';
import { RequestSupplyChangePriceModal } from 'entities/Request/components/RequestSupplyChangePriceModal';
import { checkUniformStatusForRequestSupplies } from 'entities/Request/Request.helper';
import { IRequest } from 'entities/Request/Request.models';
import { ESupplyStatus } from 'entities/Supply/Supply.enum';
import { getSupplyStatus } from 'entities/Supply/Supply.helper';
import { updateRequestSupplies } from 'entities/Request/Request.slice';
import {
  changeSupplyStatus,
  selectIsSupplyLoading,
  splitSupply,
  updateSupply,
  updateSupplyDeliveryDate,
} from 'entities/Supply/Supply.slice';

interface IComponentProps {
  request: IRequest;
  selectedSupplies: ISupplyShort[];
  translateYOffsetPx?: number;
  onSelectedSuppliesChange: (value: ISupplyShort[]) => void;
}

export const RequestActions: FC<IComponentProps> = ({
  request,
  selectedSupplies,
  translateYOffsetPx = 0,
  onSelectedSuppliesChange,
}) => {
  const dispatch = useAppDispatch();
  // supply
  const isSupplyLoading = useAppSelector(selectIsSupplyLoading);

  const [suppliesToChangeDeliveryDate, setSuppliesToChangeDeliveryDate] = useState<ISupplyShort[]>([]);
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const [openWarningCard, setOpenWarningCard] = useState<boolean>(false);
  const [openRequestSupplyChangeDeliveryDateModal, setOpenRequestSupplyChangeDeliveryDateModal] = useState<boolean>(false);
  const [openRequestSupplyChangePriceModal, setOpenRequestSupplyChangePriceModal] = useState<boolean>(false);
  const { isManager } = UserHooks.useCurrentUserRole();

  const selectedSuppliesIds = selectedSupplies.map((supply) => supply.id);
  const canSplitSupply = suppliesToChangeDeliveryDate.length === DEFAULT_VALUE_1 && !suppliesToChangeDeliveryDate[0].deliveryDate;
  const { isUniformStatus: suppliesHaveUniformStatus, uniformStatus } = checkUniformStatusForRequestSupplies(selectedSupplies);
  const { isCreatedSupply, isPendingSupply, isProcessingSupply, isApprovedSupply, isDeliverySupply } =
    getSupplyStatus(uniformStatus);

  const onSuppliesStatusChange = (value: ESupplyStatus) => {
    if (request) {
      if (
        isManager &&
        selectedSuppliesIds.length === request.supplies.length &&
        selectedSuppliesIds.length > DEFAULT_VALUE_1 &&
        value === ESupplyStatus.Cancelled
      ) {
        setOpenWarningCard(true);
        return;
      }

      dispatch(
        changeSupplyStatus({
          requestId: request.id,
          ids: selectedSuppliesIds,
          status: value,
          onSuccess: () => {
            onSelectedSuppliesChange([]);

            if (value === ESupplyStatus.Delivered) {
              const updatedSupplies = request.supplies.map((item) => {
                if (selectedSuppliesIds.includes(item.id)) {
                  return {
                    ...item,
                    status: value,
                  };
                }

                return item;
              });

              dispatch(updateRequestSupplies(updatedSupplies));
            }
          },
        }),
      );
    }
  };

  const onDeliveryDateChange = (records: ISupplyShort[]) => {
    setSuppliesToChangeDeliveryDate(records);
    setOpenRequestSupplyChangeDeliveryDateModal(true);
  };

  const onRequestSupplyChangeDeliveryDateModalApplyClick = (deliveryDate: string, count: number, onSuccess: () => void) => {
    if (canSplitSupply) {
      dispatch(
        splitSupply({
          supplyId: suppliesToChangeDeliveryDate[0].id,
          deliveryDate,
          count,
          onSuccess: () => {
            onSelectedSuppliesChange([]);
            onSuccess();
          },
        }),
      );
      return;
    }

    const isProcessingSupplies = suppliesToChangeDeliveryDate.some((supply) => supply.status === ESupplyStatus.Processing);
    const supplyIds = suppliesToChangeDeliveryDate.map((supply) => supply.id);

    if (isProcessingSupplies) {
      dispatch(
        updateSupplyDeliveryDate({
          // @ts-ignore
          supplyIds,
          deliveryDate,
          onSuccess: () => {
            dispatch(
              changeSupplyStatus({
                requestId: request.id,
                ids: supplyIds,
                status: ESupplyStatus.Approved,
                onSuccess: () => {
                  onSelectedSuppliesChange([]);
                  onSuccess();
                },
              }),
            );
          },
        }),
      );
    } else {
      dispatch(
        updateSupplyDeliveryDate({
          // @ts-ignore
          supplyIds,
          deliveryDate,
          onSuccess: () => {
            onSelectedSuppliesChange([]);
            onSuccess();
          },
        }),
      );
    }
  };

  const onRequestSupplyChangePriceModalApplyClick = (value: number, onSuccess: () => void) => {
    dispatch(
      updateSupply({
        id: selectedSuppliesIds[0],
        price: value,
        onSuccess: () => {
          onSelectedSuppliesChange([]);
          onSuccess();
        },
      }),
    );
  };

  const renderActions = () => {
    if (!suppliesHaveUniformStatus) {
      return null;
    }

    switch (true) {
      case isCreatedSupply: {
        return (
          <>
            <Button
              className="floating-action-bar__btn"
              icon={<CompletedIcon className="icon-completed-white" />}
              onClick={() => onSuppliesStatusChange(ESupplyStatus.Pending)}
              loading={isSupplyLoading}
            >
              Согласовать
            </Button>

            <Button
              className="floating-action-bar__btn red"
              icon={<CancelledIcon className="icon-cancelled-red" />}
              onClick={() => onSuppliesStatusChange(ESupplyStatus.Cancelled)}
              loading={isSupplyLoading}
            >
              Отклонить
            </Button>
          </>
        );
      }
      case isPendingSupply: {
        return (
          <Button
            className="floating-action-bar__btn red"
            icon={<CancelledIcon className="icon-cancelled-red" />}
            onClick={() => onSuppliesStatusChange(ESupplyStatus.Created)}
            loading={isSupplyLoading}
          >
            Отменить согласование
          </Button>
        );
      }
      case isProcessingSupply: {
        return (
          <>
            <Button
              className="floating-action-bar__btn"
              icon={<CalendarIcon className="icon-calendar-white" />}
              onClick={() => onDeliveryDateChange(selectedSupplies)}
            >
              Назначить поставку
            </Button>

            {selectedSupplies.length === DEFAULT_VALUE_1 && (
              <Button
                className="floating-action-bar__btn"
                icon={<EditIcon className="icon-edit-white" />}
                onClick={() => setOpenRequestSupplyChangePriceModal(true)}
              >
                Корректировать
              </Button>
            )}

            <Button
              className="floating-action-bar__btn red"
              icon={<CancelledIcon className="icon-cancelled-red" />}
              onClick={() => onSuppliesStatusChange(ESupplyStatus.Cancelled)}
              loading={isSupplyLoading}
            >
              Отказаться
            </Button>
          </>
        );
      }
      case isApprovedSupply:
      case isDeliverySupply: {
        return (
          <>
            {isApprovedSupply && selectedSupplies.length === DEFAULT_VALUE_1 && (
              <Button
                className="floating-action-bar__btn"
                icon={<EditIcon className="icon-edit-white" />}
                onClick={() => setOpenRequestSupplyChangePriceModal(true)}
              >
                Корректировать
              </Button>
            )}

            <Button
              className="floating-action-bar__btn"
              icon={<CalendarIcon className="icon-calendar-white" />}
              onClick={() => onDeliveryDateChange(selectedSupplies)}
            >
              Изменить дату
            </Button>

            <Popover
              overlayClassName="floating-action-bar__popover"
              trigger="click"
              open={openPopover}
              onOpenChange={setOpenPopover}
              content={
                <div className="floating-action-bar__popover-container">
                  {isApprovedSupply && (
                    <>
                      <Button
                        className="floating-action-bar__btn"
                        icon={<DeliveryIcon className="icon-delivery-yellow" />}
                        onClick={() => {
                          onSuppliesStatusChange(ESupplyStatus.Delivery);
                          setOpenPopover(false);
                        }}
                      >
                        Заказ в пути
                      </Button>

                      <Divider background="#464C52" marginY={8} />
                    </>
                  )}

                  <Button
                    className="floating-action-bar__btn"
                    icon={<DeliveryIcon className="icon-delivery-bright-green" />}
                    onClick={() => {
                      onSuppliesStatusChange(ESupplyStatus.Delivered);
                      setOpenPopover(false);
                    }}
                  >
                    Заказ доставлен
                  </Button>
                </div>
              }
            >
              <Button
                className="floating-action-bar__btn"
                icon={<DeliveryIcon className="icon-delivery-white" />}
                loading={isSupplyLoading}
              >
                <span>Изменить статус</span>

                {openPopover ? (
                  <ArrowUpShortIcon className="icon-arrow-up-short-white" />
                ) : (
                  <ArrowDownShortIcon className="icon-arrow-down-short-white" />
                )}
              </Button>
            </Popover>

            <Button
              className="floating-action-bar__btn red"
              icon={<CancelledIcon className="icon-cancelled-red" />}
              onClick={() => onSuppliesStatusChange(ESupplyStatus.Cancelled)}
              loading={isSupplyLoading}
            >
              Отказаться
            </Button>
          </>
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <>
      <FloatingActionBar
        selectedCount={selectedSupplies.length}
        count={request.supplies.length}
        translateYOffsetPx={translateYOffsetPx}
      >
        {renderActions()}
      </FloatingActionBar>

      <RequestSupplyChangeDeliveryDateModal
        open={openRequestSupplyChangeDeliveryDateModal}
        supplies={suppliesToChangeDeliveryDate}
        canSplitSupply={canSplitSupply}
        loading={isSupplyLoading}
        onClose={() => setOpenRequestSupplyChangeDeliveryDateModal(false)}
        onApply={onRequestSupplyChangeDeliveryDateModalApplyClick}
      />

      <RequestSupplyChangePriceModal
        open={openRequestSupplyChangePriceModal}
        defaultValue={selectedSupplies[0]?.price}
        loading={isSupplyLoading}
        onClose={() => setOpenRequestSupplyChangePriceModal(false)}
        onApply={onRequestSupplyChangePriceModalApplyClick}
      />

      <WarningCard
        open={openWarningCard}
        content="Вы уверены, что хотите отменить все позиции?"
        subtitle="В этом случае заказ перейдет в статус “Отменен” без возможности повторного согласования."
        loading={isSupplyLoading}
        confirmBtnTitle="Отменить все"
        onConfirm={() => {
          if (request) {
            dispatch(
              changeSupplyStatus({
                requestId: request.id,
                ids: selectedSuppliesIds,
                status: ESupplyStatus.Cancelled,
                onSuccess: () => {
                  onSelectedSuppliesChange([]);
                  setOpenWarningCard(false);
                },
              }),
            );
          }
        }}
        onClose={() => setOpenWarningCard(false)}
      />
    </>
  );
};
